import React, {
    useContext,
    useState,
} from 'react';
import { useMutation } from '@tanstack/react-query';

import { getCustomer, refetchCustomer } from "../queries/global";
import { NotificationContext, handleError } from '../context/NotificationContext';

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";

import AccountNavigation from './account-navigation';
import UpsellBlock from '../components/upsell-block';
import LogoutButton from '../components/logout-button';
import PreferencesBlock from './preferences-block';
import LoadingIndicator from '../components/loading-indicator';

const AccountPreferences = () => {

    const {notification, setNotification} = useContext(NotificationContext);
    const { data: customer, isFetching: customerFetching } = getCustomer();

    const [editMode, setEditMode] = useState(false);

    const replaceContactPreferences = useMutation({
        mutationFn: (preferences) => QUERIES.replaceContactPreferences(preferences),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchCustomer();
        }
    });

    const removeContactPreference = useMutation({
        mutationFn: (id) => QUERIES.removeContactPreference(id),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchCustomer();
        }
    });

    const updateContactPreferences = (formData) => {
        const data = formData.getAll("statement");
        if (!data) return;

        // Handle all checkboxes being unchecked
        if (!data.length) {
            customer.allStatements.forEach((statement) => {
                if (statement.agreed) removeContactPreference.mutateAsync(statement.id);
            });
            return;
        }

        // Handle some checked
        const new_data = customer.allStatements.map((statement) => {
            if (data.includes(statement.id)) {
                return {
                    "id": statement.id
                };
            }
        })
        .filter((id) => id !== undefined);
        replaceContactPreferences.mutateAsync(new_data);
    }
    
    return (
        <>
            <AccountNavigation />

            <div className='booking-pathway__container booking-pathway__account booking-pathway__account--preferences'>

                <div className='booking-pathway__main-content'>
                    <h1 className='booking-pathway__header'>Preferences</h1>

                    <PreferencesBlock />

                    {customer?.allStatements ?
                        <section className="section contact-preferences">
                            <h2 className="booking-pathway__header">Contact preferences</h2>
                            <p className='booking-pathway__introduction'>I'd love to hear about shows, events, products and schemes, including memberships.</p>
                            
                            {customerFetching || replaceContactPreferences.isPending ?
                                <LoadingIndicator />
                            :
                            !editMode ?
                                <>
                                    <div className="contact-preferences__static">
                                        {customer.allStatements.map((statement) => (
                                            <p key={statement.id}>{statement.text} - <span className="sr-only">Have you agreed?</span>{statement.agreed ? "Yes" : "No"}</p>
                                        ))}
                                    </div>
                                    <button type="button" className="btn btn--primary" onClick={() => setEditMode(true)}>Edit</button>
                                </>
                            :
                                <>
                                    <form className="contact-preferences__form" action={updateContactPreferences}>
                                        {customer.allStatements.map((statement) => (
                                            <div key={statement.id} className="form-field--checkbox">
                                                <input type="checkbox" 
                                                        name="statement" 
                                                        id={statement.id}
                                                        value={statement.id}
                                                        defaultChecked={statement.agreed} />
                                                <label htmlFor={statement.id}>By {statement.text}</label>
                                            </div>
                                        ))}
                                        <input type="submit" value="Submit" className="btn btn--primary" />
                                    </form>
                                    <button type="button" className="btn" onClick={() => setEditMode(false)}>Cancel</button>
                                </>
                            }
                        </section>
                    :null}
                </div>

                <div className='booking-pathway__footer'>
                    <LogoutButton />

                    <UpsellBlock content={config.OPTION_FIELDS.account?.preferences_upsell} type="preferences-upsell" />
                </div>

            </div>
        </>
    );
};

export default AccountPreferences;
